import { Input, InputProps } from '@chakra-ui/react';
import { Phone } from 'lucide-react';
import { ForwardedRef, forwardRef } from 'react';
import HierarchyTag from '../tags/hierarchy-tag';
import { InputGroup } from '../ui/input-group';

export interface PhoneInputProps extends Omit<InputProps, 'placeholder' | 'type' | 'maxLength'> {
    primary?: boolean;
    showIcon?: boolean;
    iconSize?: number;
}

const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    const length = phoneNumber.length;

    if (length < 4) {
        return phoneNumber;
    }
    if (length < 7) {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }

    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const phoneInput = (
    { primary, showIcon = true, iconSize = 16, value, onChange, ...inputProps }: PhoneInputProps,
    ref: ForwardedRef<HTMLInputElement | null>,
) => {
    const formattedValue = formatPhoneNumber(value ? String(value) : '');

    return (
        <InputGroup
            width="full"
            startElement={
                showIcon && (
                    <div style={{ paddingRight: '8px' }}>
                        {' '}
                        {/* Add padding to the right of the icon */}
                        <Phone data-testid="icon" size={iconSize} /> {/* Use iconSize prop */}
                    </div>
                )
            }
            endElement={primary && <HierarchyTag data-testid="primary-badge">primary</HierarchyTag>}>
            <Input
                {...inputProps}
                ref={ref}
                type="text"
                data-testid="phone-input-field"
                placeholder="(XXX) XXX-XXXX"
                value={formattedValue}
                onChange={(e) => {
                    const unformattedValue = e.target.value.replace(/\D/g, '');
                    if (unformattedValue.length > 10) {
                        return;
                    }
                    e.target.value = unformattedValue.slice(0, 10);
                    onChange?.(e);
                }}
            />
        </InputGroup>
    );
};

phoneInput.displayName = 'PhoneInput';

export const PhoneInput = forwardRef(phoneInput);
